import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { API_URL } from '../lib/api';
import {
  getStoredReferralCode,
  clearStoredReferralCode,
} from '../lib/referral';

const REFERRAL_CLAIM_RETRY_DELAYS_MS = [1_000, 3_000] as const;

/**
 * Claim a stored referral code after the user authenticates. Transient request
 * failures retry twice without blocking the signed-in experience.
 */
export function useReferralClaim() {
  const { isAuthenticated, isLoading } = useAuth();
  const claimed = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || claimed.current) return;

    const referralCode = getStoredReferralCode();
    if (!referralCode) return;

    claimed.current = true;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const claimReferral = async (attempt: number): Promise<void> => {
      try {
        const res = await fetch(`${API_URL}/api/v2/referrals/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ referralCode }),
        });

        if (!res.ok) {
          throw new Error(`Referral claim failed with status ${res.status}`);
        }

        const result = (await res.json()) as { claimed?: boolean };
        if (result.claimed === true) {
          clearStoredReferralCode();
        } else {
          // A semantic rejection is not transient. Retain attribution so a
          // later page load can try again if the partner becomes eligible.
          claimed.current = false;
        }
      } catch (error) {
        if (cancelled) return;

        const retryDelay = REFERRAL_CLAIM_RETRY_DELAYS_MS[attempt];
        if (retryDelay !== undefined) {
          retryTimer = setTimeout(() => {
            void claimReferral(attempt + 1);
          }, retryDelay);
          return;
        }

        claimed.current = false;
        console.warn('[Referral] Claim failed after retries', error);
      }
    };

    void claimReferral(0);

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      claimed.current = false;
    };
  }, [isAuthenticated, isLoading]);
}

/**
 * Fetch the current user's referral partner status and link.
 */
export function useReferralPartner() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['referral-partner'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v2/referrals/link`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch referral partner');
      return res.json() as Promise<{
        isPartner: boolean;
        referralCode: string | null;
        link: string | null;
        commissionRate?: number;
        referredBonusRate?: number;
        referredFlatCredits?: number | null;
        commissionMonths?: number;
        hasActiveSubscription?: boolean;
      }>;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch the current user's referral stats and referral list.
 */
export function useReferralStats() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['referral-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v2/referrals/stats`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch referral stats');
      return res.json() as Promise<{
        stats: {
          totalReferrals: number;
          activeReferrals: number;
          pendingReferrals: number;
          churnedReferrals: number;
          totalCreditsEarned: number;
          monthlyBonusCredits: number;
        };
        referrals: Array<{
          id: string;
          referredEmail: string;
          planName: string | null;
          status: string;
          creditsEarned: number;
          monthlyBonus: number;
          monthsRemaining: number | null;
          createdAt: string;
        }>;
      }>;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Shared "new" indicator for the Referrals tab.
 * Uses localStorage so the dot persists across page loads and
 * useSyncExternalStore so all mounted components update together
 * when either the sidebar or the user menu is clicked.
 */
export const REFERRALS_SEEN_KEY = 'miles_referrals_tab_seen';

const listeners = new Set<() => void>();

function getSnapshot() {
  return !localStorage.getItem(REFERRALS_SEEN_KEY);
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function markReferralsSeen() {
  localStorage.setItem(REFERRALS_SEEN_KEY, '1');
  listeners.forEach((cb) => cb());
}

export function useReferralsNew() {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
